import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, prefetchCriticalData, invalidateAllData, setValidatingSession } from "@/lib/queryClient";
import { User, AuthContextType } from "@/lib/types";
import { useToast } from "@/hooks/use-toast";
import { supabase, getSupabaseSession } from "@/lib/supa";
import { csrfService } from "@/lib/csrfService";
import { setSentryUser } from "@/lib/sentry";
import { env } from "@/lib/environment";
import { useOAuth } from "@/contexts/OAuthContext";
import { posthog } from "@/lib/posthog";
import { isSafari, getOAuthProcessingDelay } from "@/utils/browser-detection";

// Create auth context
const AuthContext = createContext<AuthContextType>({
  user: null,
  error: null,
  isLoading: true,
  login: () => {},
  register: () => {},
  logout: () => {},
  updateUser: () => {},
  clearError: () => {},
  setOAuthContext: () => {}
});

// Industry-standard session management
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check session every 5 minutes
const SESSION_WARNING_TIME = 5 * 60 * 1000; // Warn 5 minutes before timeout
const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour timeout
const SESSION_STORAGE_KEY = "sessionId";
const USER_STORAGE_KEY = "user";
const LAST_ACTIVITY_KEY = "lastActivity";

// Provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const OAUTH_CONTEXT_STORAGE_KEY = 'oauthContext';

  // Initialize oauth context from localStorage so it persists across full page reloads (e.g., OAuth redirect)
  const [oauthContext, setOAuthContextState] = useState<'login' | 'register'>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(OAUTH_CONTEXT_STORAGE_KEY);
      if (stored === 'register' || stored === 'login') {
        return stored;
      }
    }
    return 'login';
  });

  // Helper to update oauthContext and persist it
  const updateOAuthContext = useCallback((ctx: 'login' | 'register') => {
    setOAuthContextState(ctx);
    if (typeof window !== 'undefined') {
      localStorage.setItem(OAUTH_CONTEXT_STORAGE_KEY, ctx);
    }
  }, []);

  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();
  const { 
    startOAuthProcessing, 
    completeOAuthProcessing, 
    setOAuthError, 
    getProcessingSessionId,
    hasProcessedEvent
  } = useOAuth();
  
  // Helper function to clear all session data
  const clearSession = useCallback(() => {
    setUser(null);
    setError(null);
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    
    // Clear CSRF token
    csrfService.clearToken();
    
    // Clear Sentry user context
    setSentryUser(null);
    
    invalidateAllData();
  }, []);
  
  // Validate session with server
  const validateSession = useCallback(async () => {
    const sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) return false;
    
    try {
      const response = await apiRequest("GET", "/api/preferences/", null, {
        headers: {
          Authorization: `Bearer ${sessionId}`
        }
      });
      
      // Only consider 200 as valid, everything else is invalid
      return response.status === 200;
    } catch {
      // Network errors = keep session (offline tolerance)
      return true;
    }
  }, []);
  
  // Session check function (runs periodically)
  const checkSession = useCallback(async () => {
    const isValid = await validateSession();
    
    if (!isValid) {
      // Session is invalid, clear it
      clearSession();
      toast({
        title: "Session Expired",
        description: "Please log in again to continue.",
        variant: "destructive",
      });
    }
  }, [validateSession, clearSession, toast]);
  
  // Handle OAuth callback
  const handleOAuthCallback = useCallback(async (supabaseSession: any, isRegistration = false, eventType = 'unknown') => {
    try {
      // Check if we're already processing this session to prevent duplicates
      const sessionId = supabaseSession.access_token?.substring(0, 20);
      const currentProcessingId = getProcessingSessionId();
      
      if (currentProcessingId === sessionId) {
        console.log("Already processing this OAuth session via OAuth context, skipping...");
        return;
      }
      
      console.log("=== Starting OAuth Callback ===");
      console.log("Session access token length:", supabaseSession.access_token?.length);
      console.log("Session user email:", supabaseSession.user?.email);
      console.log("Is registration:", isRegistration);
      console.log("OAuth context:", oauthContext);
      
      // Determine provider from session metadata
      const detectedProvider = supabaseSession.user?.app_metadata?.provider || 'unknown';
      console.log("Detected OAuth provider:", detectedProvider);
      
      // Set processing flag in centralized OAuth context with event type
      const shouldProcess = startOAuthProcessing(sessionId, detectedProvider, eventType);
      if (!shouldProcess) {
        console.log(`OAuth processing already in progress or recently completed for event ${eventType}, skipping`);
        return;
      }
      
      // Mark that we're validating the session
      setValidatingSession(true);
      
      // Extract provider tokens - these are the actual tokens for Gmail/Outlook APIs
      // Check multiple possible locations for the provider tokens
      let providerToken = supabaseSession.provider_token || 
                         supabaseSession.provider_tokens?.google?.access_token ||
                         supabaseSession.provider_tokens?.azure?.access_token;
      let providerRefreshToken = supabaseSession.provider_refresh_token || 
                                supabaseSession.provider_tokens?.google?.refresh_token ||
                                supabaseSession.provider_tokens?.azure?.refresh_token;
      
      // Check sessionStorage for temporarily stored provider tokens from URL
      if (!providerToken) {
        const tempProviderToken = sessionStorage.getItem('temp_provider_token');
        if (tempProviderToken) {
          console.log("Using provider token from sessionStorage (captured from URL)");
          providerToken = tempProviderToken;
          sessionStorage.removeItem('temp_provider_token'); // Clean up
        }
      }
      
      if (!providerRefreshToken) {
        const tempProviderRefreshToken = sessionStorage.getItem('temp_provider_refresh_token');
        if (tempProviderRefreshToken) {
          console.log("Using provider refresh token from sessionStorage (captured from URL)");
          providerRefreshToken = tempProviderRefreshToken;
          sessionStorage.removeItem('temp_provider_refresh_token'); // Clean up
        }
      }
      
      // IMPORTANT: Provider tokens are only available on the initial OAuth callback
      // They are NOT persisted in subsequent getSession() calls for security reasons
      // If we don't have provider tokens, we'll need to handle token refresh on the backend
      if (!providerToken) {
        console.warn("Provider tokens not available in session - this is expected for existing sessions");
        console.warn("The backend will need to use refresh tokens to get new access tokens");
      }
      
      // Extract scopes from the session
      let scopes = null;
      if (supabaseSession.user?.app_metadata?.provider_scopes) {
        scopes = supabaseSession.user.app_metadata.provider_scopes;
      } else if (supabaseSession.provider_scopes) {
        scopes = supabaseSession.provider_scopes;
      } else if (detectedProvider === 'google') {
        // Fallback to expected Google scopes if not available in session
        scopes = 'email openid profile https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send';
      } else if (detectedProvider === 'azure') {
        // Fallback to expected Microsoft scopes if not available in session
        scopes = 'email openid profile offline_access https://graph.microsoft.com/Mail.Read https://graph.microsoft.com/Mail.Send';
      }
      
      // Log the session structure to debug token locations
      console.log("=== Supabase Session Structure Debug ===");
      console.log("Session keys:", Object.keys(supabaseSession));
      console.log("Session.provider_token:", supabaseSession.provider_token);
      console.log("Session.provider_refresh_token:", supabaseSession.provider_refresh_token);
      console.log("Session.provider_tokens:", supabaseSession.provider_tokens);
      console.log("User metadata:", supabaseSession.user?.app_metadata);
      
      // If provider tokens aren't available from Supabase session, 
      // use the Supabase tokens as fallback (they should work for basic API access)
      console.log("Provider token availability:", {
        hasProviderToken: !!providerToken,
        hasProviderRefreshToken: !!providerRefreshToken,
        hasSupabaseToken: !!supabaseSession.access_token,
        hasSupabaseRefresh: !!supabaseSession.refresh_token,
        scopes: scopes,
        providerTokenPreview: providerToken ? providerToken.substring(0, 20) + "..." : "null"
      });
      
      // Check if user exists and sync session with backend
      const apiEndpoint = isRegistration ? "/api/auth/oauth-register" : "/api/auth/oauth-callback";
      console.log("Making request to:", apiEndpoint);
      
      const response = await apiRequest("POST", apiEndpoint, {
        provider: detectedProvider,
        access_token: supabaseSession.access_token,
        refresh_token: supabaseSession.refresh_token,
        provider_token: providerToken || supabaseSession.access_token,
        provider_refresh_token: providerRefreshToken || supabaseSession.refresh_token,
        scopes: scopes,  // Include scopes in the request
        user: {
          id: supabaseSession.user.id,
          email: supabaseSession.user.email,
          name: supabaseSession.user.user_metadata.full_name || supabaseSession.user.user_metadata.name,
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Backend OAuth sync failed:", response.status, errorText);
        console.error("Request details:", {
          endpoint: apiEndpoint,
          isRegistration,
          provider: detectedProvider,
          userEmail: supabaseSession.user.email
        });
        
        // Check if this is a "user not found" error (403) for login
        if (response.status === 403 && !isRegistration) {
          // Clear processing flag
          completeOAuthProcessing();
          setValidatingSession(false);
          
          // Sign out from Supabase to clear the session
          await supabase.auth.signOut();
          
          console.log("=== OAuth Error Debug ===");
          console.log("Detected provider:", detectedProvider);
          console.log("User email:", supabaseSession.user.email);
          console.log("Setting error state...");
          
          // Set a specific error state that the UI can handle specially
          const errorState = `OAUTH_NO_ACCOUNT:${detectedProvider}:${supabaseSession.user.email}`;
          console.log("Error state being set:", errorState);
          setError(errorState);
          
          // Also show a toast for immediate feedback
          toast({
            title: "Account Required",
            description: `No account found for ${supabaseSession.user.email}. Please register first.`,
            variant: "destructive",
          });
          
          // Prevent any further processing
          return;
        }
        
        // Check if this is a "user already exists" error (409) for registration
        if (response.status === 409 && isRegistration) {
          // Clear processing flag
          completeOAuthProcessing();
          setValidatingSession(false);
          
          // Sign out from Supabase to clear the session
          await supabase.auth.signOut();
          
          console.log("=== OAuth Registration Error Debug ===");
          console.log("Detected provider:", detectedProvider);
          console.log("User email:", supabaseSession.user.email);
          
          // Set a specific error state for existing account during registration
          setError(`OAUTH_ACCOUNT_EXISTS:${detectedProvider}:${supabaseSession.user.email}`);
          
          // Prevent any further processing
          return;
        }
        
        // For registration failures, provide more specific error handling
        if (isRegistration) {
          console.error("OAuth registration failed:", {
            status: response.status,
            error: errorText,
            provider: detectedProvider,
            email: supabaseSession.user.email
          });
          
          // Clear processing flag
          completeOAuthProcessing();
          setValidatingSession(false);
          
          // Sign out from Supabase to clear the session
          await supabase.auth.signOut();
          
          // Show a more specific error message
          setError(`Registration failed: ${errorText || 'Unknown error'}`);
          
          toast({
            title: "Registration Failed",
            description: `Failed to create account with ${detectedProvider}. Please try again or use email registration.`,
            variant: "destructive",
          });
          
          // Prevent any further processing
          return;
        }
        
        throw new Error(`Failed to sync OAuth session with backend: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log("Backend OAuth sync response:", data);
      
      // Check if payment is required
      if (data.payment_required) {
        console.log("Payment required for new user");
        // Store user data but don't store session if payment required
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
        setUser(data.user);
        setError(null);
        
        // Don't navigate here - let the component handle it
        return;
      }
      
      // Store session data - PRIORITY: Use backend-provided session tokens
      // Backend now returns backend-compatible tokens for OAuth registration
      let sessionToken;
      if (data.access_token || data.sessionId) {
        // Use backend-provided session token (compatible with our API)
        sessionToken = data.access_token || data.sessionId;
        console.log("Using backend-provided session token:", sessionToken?.substring(0, 20) + "...");
      } else {
        // Fallback to original OAuth token (for backwards compatibility)
        sessionToken = supabaseSession.access_token;
        console.log("Falling back to original OAuth token:", sessionToken?.substring(0, 20) + "...");
      }
      
      localStorage.setItem(SESSION_STORAGE_KEY, sessionToken);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
      
      // Update state - ONLY clear error if we successfully completed OAuth
      setUser(data.user);
      setError(null);
      
      // Set Sentry user context
      setSentryUser(data.user);
      
      // Re-identify user in PostHog with actual email after OAuth completion
      if (data.user?.email) {
        console.log('OAuth: Re-identifying user in PostHog with actual email:', data.user.email);
        posthog.identify(data.user.email, {
          email: data.user.email,
          name: data.user.name,
          username: data.user.username,
          school: data.user.school,
          oauth_provider: data.user.oauth_provider,
          $email: data.user.email,
          $name: data.user.name || data.user.email.split('@')[0],
        });
      }
      
      // Test the stored token immediately
      console.log("Testing stored token with backend...");
      try {
        const testResponse = await apiRequest("GET", "/api/preferences/", null, {
          headers: {
            Authorization: `Bearer ${sessionToken}`
          }
        });
        console.log("Token test result:", testResponse.status);
        if (!testResponse.ok) {
          console.error("Token validation failed immediately after OAuth");
        }
      } catch (tokenTestError) {
        console.error("Token test error:", tokenTestError);
      }
      
      // Set up session checking
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
      sessionCheckIntervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL);
      
      // Clear validation state
      setValidatingSession(false);
      
      // Prefetch data for better UX
      prefetchCriticalData().catch(console.warn);
      
      // Clear processing flag after successful completion
      completeOAuthProcessing();
      
      // After successful login/registration, reset OAuth context to 'login'
      updateOAuthContext('login');
      
    } catch (err: any) {
      console.error("OAuth callback error:", err);
      const errorMessage = err.message || "Failed to complete OAuth login";
      setError(errorMessage);
      setOAuthError(errorMessage);
      setValidatingSession(false); // Clear validation state on error
      clearSession();
      
      // Clear processing flag on error
      completeOAuthProcessing();
      
      // Sign out from Supabase on error to clear any partial session
      await supabase.auth.signOut().catch(console.error);
      
      toast({
        title: "Authentication Error",
        description: "Failed to complete sign in. Please try again.",
        variant: "destructive",
      });
    }
  }, [checkSession, clearSession, toast, oauthContext, updateOAuthContext, startOAuthProcessing, completeOAuthProcessing, setOAuthError, getProcessingSessionId, hasProcessedEvent]);
  
  // Listen to Supabase auth state changes
  useEffect(() => {
    // Check if Supabase is configured
    if (!env.supabaseUrl || !env.supabaseAnonKey) {
      console.log("Supabase not configured, OAuth will be disabled");
      return;
    }
    
    console.log("Setting up Supabase auth state listener...");
    
    // Manual session check function for debugging
    const manualSessionCheck = async () => {
      console.log("=== Manual Session Check ===");
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log("Manual getSession result:", {
          hasSession: !!session,
          error: error?.message,
          user_email: session?.user?.email,
          provider: session?.user?.app_metadata?.provider,
          oauthContext
        });
        
        if (session && !error) {
          // Check if we're already processing this session
          const sessionId = session.access_token?.substring(0, 20);
          const currentProcessingId = getProcessingSessionId();
          if (currentProcessingId === sessionId) {
            console.log("Already processing this session via OAuth context, skipping manual check");
            return;
          }
          
          console.log("Manual session check found valid session, calling handleOAuthCallback...");
          const isRegistration = oauthContext === 'register';
          await handleOAuthCallback(session, isRegistration, 'MANUAL_CHECK');
        }
      } catch (err) {
        console.error("Manual session check error:", err);
      }
    };
    
    // Trigger manual check immediately
    // Disabled to prevent duplicate OAuth processing
    // manualSessionCheck();
    
    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("=== Supabase Auth State Change ===");
      console.log("Event:", event);
      console.log("Session exists:", !!session);
      console.log("Current OAuth Context:", oauthContext);
      console.log("Session details:", session ? {
        user_id: session.user?.id,
        user_email: session.user?.email,
        access_token_length: session.access_token?.length,
        provider: session.user?.app_metadata?.provider,
        expires_at: session.expires_at
      } : null);
      
      if (event === 'SIGNED_IN' && session) {
        console.log("Detected SIGNED_IN event");
        
        // Safari: Add delay to ensure storage operations complete
        if (isSafari()) {
          const delay = getOAuthProcessingDelay();
          console.log(`[Safari] Adding ${delay}ms delay before processing SIGNED_IN event`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        // Check if we're already processing this session
        const sessionId = session.access_token?.substring(0, 20);
        const currentProcessingId = getProcessingSessionId();
        if (currentProcessingId === sessionId) {
          console.log("Already processing this session via OAuth context, skipping SIGNED_IN");
          return;
        }
        
        // Check if we've already processed this specific event for this session
        if (hasProcessedEvent(sessionId, 'SIGNED_IN')) {
          console.log("Already processed SIGNED_IN event for this session, skipping");
          return;
        }
        
        // Check if we already have an OAuth error - if so, don't process again
        if (error && (error.startsWith('OAUTH_NO_ACCOUNT:') || error.startsWith('OAUTH_ACCOUNT_EXISTS:'))) {
          console.log("Already have OAuth error, skipping SIGNED_IN processing:", error);
          return;
        }
        
        // Handle OAuth sign in - use context to determine if registration or login
        const isRegistration = oauthContext === 'register';
        console.log("Auth state change - isRegistration:", isRegistration, "context:", oauthContext);
        await handleOAuthCallback(session, isRegistration, 'SIGNED_IN');
      } else if (event === 'SIGNED_OUT') {
        console.log("Detected SIGNED_OUT event, clearing session...");
        // Handle sign out
        clearSession();
      } else if (event === 'INITIAL_SESSION' && session) {
        console.log("Detected INITIAL_SESSION with session");
        
        // Check if we're already processing this session
        const sessionId = session.access_token?.substring(0, 20);
        const currentProcessingId = getProcessingSessionId();
        if (currentProcessingId === sessionId) {
          console.log("Already processing this session via OAuth context, skipping INITIAL_SESSION");
          return;
        }
        
        // Check if we've already processed this specific event for this session
        if (hasProcessedEvent(sessionId, 'INITIAL_SESSION')) {
          console.log("Already processed INITIAL_SESSION event for this session, skipping");
          return;
        }
        
        // Check if we already have an OAuth error - if so, don't process again
        if (error && (error.startsWith('OAUTH_NO_ACCOUNT:') || error.startsWith('OAUTH_ACCOUNT_EXISTS:'))) {
          console.log("Already have OAuth error, skipping INITIAL_SESSION processing:", error);
          return;
        }
        
        // Handle initial session (e.g., after refresh or direct navigation)
        const isRegistration = oauthContext === 'register';
        console.log("Initial session - isRegistration:", isRegistration, "context:", oauthContext);
        await handleOAuthCallback(session, isRegistration, 'INITIAL_SESSION');
      } else {
        console.log("Auth state change ignored:", event, "session exists:", !!session);
      }
    });
    
    // Check for existing Supabase session on mount
    // DISABLED: This causes duplicate OAuth callbacks. The auth state listener above
    // already handles INITIAL_SESSION events, which serve the same purpose.
    // getSupabaseSession().then(session => {
    //   if (session) {
    //     const isRegistration = oauthContext === 'register';
    //     handleOAuthCallback(session, isRegistration, 'MOUNT_CHECK');
    //   }
    // }).catch(console.error);
    
    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [handleOAuthCallback, clearSession, oauthContext, getProcessingSessionId, hasProcessedEvent]);
  
  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      if (isInitialized) return;
      
      const sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
      const storedUser = localStorage.getItem(USER_STORAGE_KEY);
      
      if (sessionId && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          
          // Set user immediately for instant UI
          setUser(parsedUser);
          
          // Set Sentry user context
          setSentryUser(parsedUser);
          
          // Mark that we're validating to prevent redirects
          setValidatingSession(true);
          
          // Small delay to let components mount
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Validate session in background
          const isValid = await validateSession();
          
          // Done validating
          setValidatingSession(false);
          
          if (isValid) {
            // Session is valid, set up periodic checks
            sessionCheckIntervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL);
            
            // Prefetch data in background
            prefetchCriticalData().catch(console.warn);
          } else {
            // Session invalid, clear everything
            clearSession();
          }
        } catch (err) {
          console.error("Error initializing auth:", err);
          setValidatingSession(false);
          clearSession();
        }
      } else {
        // No stored session
        setValidatingSession(false);
      }
      
      setIsLoading(false);
      setIsInitialized(true);
    };
    
    initializeAuth();
    
    // Cleanup on unmount
    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
      setValidatingSession(false);
    };
  }, [isInitialized, validateSession, checkSession, clearSession]);
  
  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      console.log("Attempting login for:", credentials.email);
      console.log("Note: Login may take 10-15 seconds, please wait...");
      
      const response = await apiRequest("POST", "/api/auth/login", credentials);
      
      console.log("Login response status:", response.status);
      console.log("Login response headers:", Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        let errorMessage = "Failed to login";
        try {
          const errorData = await response.text();
          if (errorData) {
            errorMessage = errorData;
          }
        } catch {
          // If we can't read the error, use default message
        }
        console.error("Login failed with status:", response.status, "message:", errorMessage);
        throw new Error(errorMessage);
      }
      
      // Check if response has content
      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get('content-type');
      
      console.log("Response content-length:", contentLength);
      console.log("Response content-type:", contentType);
      
      // Try to read the response body
      let responseText = "";
      try {
        responseText = await response.text();
        console.log("Response body length:", responseText.length);
        console.log("Response body preview:", responseText.substring(0, 200));
      } catch (textError) {
        console.error("Failed to read response as text:", textError);
        throw new Error("Could not read response from server");
      }
      
      // If we have no content, that's the problem
      if (!responseText || responseText.length === 0) {
        console.error("Empty response body received from server");
        throw new Error(`Invalid response from server - no content returned (Content-Type: ${contentType}, Length: ${contentLength})`);
      }
      
      // Try to parse as JSON
      try {
        const data = JSON.parse(responseText);
        console.log("Login response data keys:", Object.keys(data || {}));
        
        if (!data) {
          throw new Error("Empty response data");
        }
        
        if (!data.user) {
          console.error("Missing user in login response:", data);
          throw new Error("Invalid response format from server");
        }
        
        // Check if payment is required (no session provided)
        if (data.payment_required && !data.sessionId) {
          console.log("Payment required for user:", data.user.email);
          return data;
        }
        
        if (!data.sessionId) {
          console.error("Missing sessionId in login response:", data);
          throw new Error("Invalid response format from server");
        }
        
        console.log("Login successful for user:", data.user.email);
        return data;
      } catch (jsonError) {
        console.error("JSON parsing error:", jsonError);
        console.error("Response text that failed to parse:", responseText);
        throw new Error("Invalid response from server - malformed JSON");
      }
    },
    onSuccess: (data) => {
      // Check if payment is required
      if (data.payment_required) {
        console.log("Payment required for user, redirecting to dashboard");
        
        // Extract session token even when payment is required
        const sessionId = data.sessionId || data.access_token;
        
        if (sessionId) {
          // Store both user data AND session for authenticated payment requests
          localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
          setUser(data.user);
          setError(null);
          
          // Set up session checking for the authenticated session
          if (sessionCheckIntervalRef.current) {
            clearInterval(sessionCheckIntervalRef.current);
          }
          sessionCheckIntervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL);
        } else {
          // No session token - store only user data
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
          setUser(data.user);
          setError(null);
        }
        
        // Don't navigate here - let the component handle it
        return;
      }
      
      // Extract session token
      const sessionId = data.sessionId || data.access_token;
      
      if (!sessionId || !data.user) {
        setError("Invalid login response from server");
        return;
      }
      
      // Store session data
      localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
      
      // Update state
      setUser(data.user);
      setError(null);
      
      // Set Sentry user context
      setSentryUser(data.user);
      
      // Set up session checking
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
      sessionCheckIntervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL);
      
      // Prefetch data for better UX
      prefetchCriticalData().catch(console.warn);
      
      // Fetch CSRF token by making a GET request
      apiRequest("GET", "/api/auth/user")
        .then(() => console.log("CSRF token fetched after login"))
        .catch(console.warn);
    },
    onError: (err: any) => {
      setError(err.message || "Failed to login");
    }
  });
  
  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async (userData: { 
      username: string; 
      password: string; 
      name: string; 
      email: string; 
      school?: string;
    }) => {
      console.log("Attempting registration for:", userData.email);
      
      const response = await apiRequest("POST", "/api/auth/register", userData);
      
      console.log("Register response status:", response.status);
      console.log("Register response headers:", Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        let errorMessage = "Failed to register";
        try {
          const errorData = await response.json();
          if (errorData?.detail) {
            errorMessage = errorData.detail;
            
            // Clean up password validation errors for better UX
            if (errorMessage.includes("Password does not meet security requirements:")) {
              // Extract just the error list part
              const errors = errorMessage.split(": ")[1];
              errorMessage = `Password requirements not met: ${errors}`;
            }
          }
        } catch {
          // If we can't read the error as JSON, try text
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch {
            // If we can't read the error at all, use default message
          }
        }
        console.error("Registration failed with status:", response.status, "message:", errorMessage);
        throw new Error(errorMessage);
      }
      
      // Check if response has content
      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get('content-type');
      
      console.log("Register response content-length:", contentLength);
      console.log("Register response content-type:", contentType);
      
      if (contentLength === '0' || !contentType?.includes('application/json')) {
        console.error("Empty or non-JSON response from register endpoint");
        throw new Error("Invalid response from server - no content returned");
      }
      
      try {
        const data = await response.json();
        console.log("Register response data keys:", Object.keys(data || {}));
        
        if (!data) {
          throw new Error("Empty response data");
        }
        
        if (!data.user) {
          console.error("Missing required fields in register response:", data);
          throw new Error("Invalid response format from server");
        }
        
        console.log("Registration successful for user:", data.user.email);
        
        return data;
      } catch (jsonError) {
        console.error("JSON parsing error:", jsonError);
        console.error("Response was not valid JSON");
        throw new Error("Invalid response from server - malformed JSON");
      }
    },
    onSuccess: (data) => {
      // Check if payment is required first
      if (data.payment_required) {
        console.log("Payment required for new registration");
        
        // Extract session token even when payment is required
        const sessionId = data.sessionId || data.access_token;
        
        if (sessionId) {
          // Store both user data AND session for authenticated payment requests
          localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
          setUser(data.user);
          setError(null);
          
          // Set up session checking for the authenticated session
          if (sessionCheckIntervalRef.current) {
            clearInterval(sessionCheckIntervalRef.current);
          }
          sessionCheckIntervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL);
        } else {
          // No session token - store only user data
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
          setUser(data.user);
          setError(null);
        }
        
        // Show success message if email confirmation is also required
        if (data.message && data.message.includes("email")) {
          toast({
            title: "Registration Successful!",
            description: data.message,
            variant: "default",
          });
        }
        
        // Don't navigate here - let the component handle it
        return;
      }
      
      // Extract session token
      const sessionId = data.sessionId || data.access_token;
      
      // Check if this is a successful registration that requires email confirmation
      if (!sessionId && data.user && data.message) {
        // Registration successful but email confirmation required
        setError(null);
        
        // Show success toast with confirmation message
        toast({
          title: "Registration Successful!",
          description: data.message || "Please check your email and click the confirmation link to complete your account setup.",
          variant: "default",
        });
        
        // Don't store session data or set user since no session was created
        // User will need to confirm email and then log in
        return;
      }
      
      // Original logic for successful registration with immediate session
      if (!sessionId || !data.user) {
        setError("Invalid registration response from server");
        return;
      }
      
      // Store session data
      localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
      
      // Update state
      setUser(data.user);
      setError(null);
      
      // Set Sentry user context
      setSentryUser(data.user);
      
      // Set up session checking
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
      sessionCheckIntervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL);
      
      // Prefetch data for better UX
      prefetchCriticalData().catch(console.warn);
      
      // Fetch CSRF token by making a GET request
      apiRequest("GET", "/api/auth/user")
        .then(() => console.log("CSRF token fetched after login"))
        .catch(console.warn);
    },
    onError: (err: any) => {
      setError(err.message || "Failed to register");
    }
  });
  
  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      const sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!sessionId) return null;
      
      try {
        const response = await apiRequest("POST", "/api/auth/logout", {}, {
          headers: {
            Authorization: `Bearer ${sessionId}`
          }
        });
        
        if (response.ok) {
          try {
            return await response.json();
          } catch {
            // If JSON parsing fails, just return null
            return null;
          }
        }
        return null;
      } catch {
        // Even if logout fails on server, clear local session
        return null;
      }
    },
    onSettled: () => {
      // Always clear session, regardless of server response
      clearSession();
      
      // Also sign out from Supabase if configured
      if (env.supabaseUrl && env.supabaseAnonKey) {
        supabase.auth.signOut().catch(console.error);
      }
    }
  });
  
  // Public API functions
  const login = useCallback((email: string, password: string) => {
    setError(null);
    
    // Clear any existing session data before attempting login
    // This prevents conflicts with old/stale sessions, especially after account deletion
    clearSession();
    
    toast({
      title: "Signing in...",
      description: "Please wait, this may take up to 15 seconds.",
    });
    loginMutation.mutate({ email, password });
  }, [loginMutation, toast, clearSession]);
  
  const register = useCallback((username: string, password: string, name: string, email: string, school?: string) => {
    setError(null);
    
    // Clear any existing session data before attempting registration
    // This ensures a clean slate for new accounts
    clearSession();
    
    toast({
      title: "Creating account...",
      description: "Please wait, this may take up to 15 seconds.",
    });
    registerMutation.mutate({ username, password, name, email, school });
  }, [registerMutation, toast, clearSession]);
  
  const logout = useCallback(() => {
    logoutMutation.mutate();
  }, [logoutMutation]);
  
  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  // Update user data in context and localStorage
  const updateUser = useCallback((userData: Partial<User>) => {
    if (!user) return;
    
    const updatedUser = { ...user, ...userData };
    setUser(updatedUser);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updatedUser));
    
    // Update Sentry user context
    setSentryUser(updatedUser);
  }, [user]);
  
  // Add global debugging functions
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).debugAuth = {
        checkSupabaseSession: async () => {
          const { data: { session }, error } = await supabase.auth.getSession();
          console.log("Debug session check:", { session, error });
          return { session, error };
        },
        triggerOAuthCallback: async () => {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            await handleOAuthCallback(session, false, 'DEBUG_TRIGGER');
          } else {
            console.log("No session found");
          }
        },
        getCurrentUser: () => user,
        getStoredSession: () => localStorage.getItem(SESSION_STORAGE_KEY),
        clearLocalStorage: () => {
          localStorage.removeItem(SESSION_STORAGE_KEY);
          localStorage.removeItem(USER_STORAGE_KEY);
          console.log("Local storage cleared");
        }
      };
    }
  }, [user, handleOAuthCallback]);
  
  // Check for OAuth redirect on mount
  useEffect(() => {
    const checkOAuthRedirect = async () => {
      // First, check if we have an authorization code in URL params
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code) {
        // Check if we've already processed this code
        const processedKey = `oauth_code_processed_${code.substring(0, 10)}`;
        if (sessionStorage.getItem(processedKey)) {
          console.log("OAuth code already processed, skipping...");
          // Clear the URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }
        
        // Mark this code as processed
        sessionStorage.setItem(processedKey, 'true');
        
        console.log("OAuth authorization code detected, exchanging for session...");
        
        try {
          // Exchange the code for a session
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          
          if (error) {
            console.error("Failed to exchange code for session:", error);
            // Clear the URL parameters even on error
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          }
          
          if (data?.session) {
            console.log("Code exchanged successfully");
            console.log("Full session structure:", JSON.stringify({
              hasProviderToken: !!data.session.provider_token,
              hasProviderRefreshToken: !!data.session.provider_refresh_token,
              provider: data.session.user?.app_metadata?.provider,
              allKeys: Object.keys(data.session),
              userMetadata: data.session.user?.user_metadata,
              appMetadata: data.session.user?.app_metadata
            }, null, 2));
            
            // Check all possible locations for provider tokens
            const providerToken = data.session.provider_token || 
                                 data.session.user?.user_metadata?.provider_token ||
                                 data.session.user?.app_metadata?.provider_token;
            
            const providerRefreshToken = data.session.provider_refresh_token || 
                                        data.session.user?.user_metadata?.provider_refresh_token ||
                                        data.session.user?.app_metadata?.provider_refresh_token;
            
            // Store provider tokens if available
            if (providerToken) {
              console.log("Storing provider token in sessionStorage");
              sessionStorage.setItem('temp_provider_token', providerToken);
            } else {
              console.warn("No provider token found in session after code exchange");
            }
            
            if (providerRefreshToken) {
              console.log("Storing provider refresh token in sessionStorage");
              sessionStorage.setItem('temp_provider_refresh_token', providerRefreshToken);
            } else {
              console.warn("No provider refresh token found in session after code exchange");
            }
            
            // Clear the code from URL to prevent re-processing
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('code');
            window.history.replaceState({}, document.title, newUrl.pathname + newUrl.hash);
            
            // Force a session refresh to ensure we have the latest data
            console.log("Forcing session refresh after code exchange...");
            const { data: refreshedSession } = await supabase.auth.getSession();
            if (refreshedSession?.session) {
              console.log("Refreshed session provider tokens:", {
                hasProviderToken: !!refreshedSession.session.provider_token,
                hasProviderRefreshToken: !!refreshedSession.session.provider_refresh_token
              });
            }
          }
        } catch (err) {
          console.error("Error during code exchange:", err);
        }
        
        return; // Don't check hash params if we had a code
      }
      
      // Fallback: Check if we have hash fragments from OAuth redirect (older flow)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      
      console.log("Checking for OAuth redirect in hash:", {
        hasHash: !!window.location.hash,
        hasAccessToken: !!accessToken,
        currentUrl: window.location.href,
        allHashParams: Array.from(hashParams.keys())
      });
      
      if (accessToken && refreshToken) {
        console.log("OAuth tokens found in URL hash, session should be auto-detected by Supabase");
        
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (error) {
            console.error("Error setting session from OAuth tokens:", error);
          } else if (data.session) {
            console.log("Session set successfully from OAuth tokens");
            // Clear the hash to clean up the URL
            window.history.replaceState(null, '', window.location.pathname);
          }
        } catch (err) {
          console.error("Failed to set session from OAuth tokens:", err);
        }
      }
      
      // Also check for existing session on mount
      const { data: { session }, error } = await supabase.auth.getSession();
      console.log("Initial session check on mount:", {
        hasSession: !!session,
        error: error?.message,
        user: session?.user?.email,
        oauthContext
      });
      
      if (session && !error) {
        // Check if we're already processing this session
        const sessionId = session.access_token?.substring(0, 20);
        const currentProcessingId = getProcessingSessionId();
        if (currentProcessingId === sessionId) {
          console.log("Already processing this session via OAuth context, skipping mount check");
          return;
        }
        
        console.log("Found existing session on mount, processing...");
        const isRegistration = oauthContext === 'register';
        await handleOAuthCallback(session, isRegistration, 'OAUTH_REDIRECT_CHECK');
      }
    };
    
    checkOAuthRedirect();
  }, [handleOAuthCallback, oauthContext, getProcessingSessionId]);
  
  return (
    <AuthContext.Provider
      value={{
        user,
        error,
        isLoading: isLoading || loginMutation.isPending || registerMutation.isPending,
        login,
        register,
        logout,
        updateUser,
        clearError,
        setOAuthContext: updateOAuthContext
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Hook for using the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}